import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { selectCurrentBusinessMode } from '../../../app/Reducers/AppConfig';
import { EBusinessType } from '../../../app/Reducers/AppConfig/types';
import { initAuth } from '../../../clients/firebase';
import { onboardingPages } from '../../../pages/OnboardingInitial';

// Define the structure for onboarded status
interface OnboardedStatus {
  pod: boolean;
  dropshipping: boolean;
  branded: boolean;
}

const PrivateRoutes = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [onboarded, setOnboarded] = useState<OnboardedStatus | null>(null);
  const navigationInProgress = useRef(false);

  const currentBusinessType = useSelector(
    selectCurrentBusinessMode
  ) as EBusinessType | null;
  const auth = initAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        // User is signed in
        const storedUser = JSON.parse(localStorage.getItem('user') as string);
        if (storedUser) setOnboarded(storedUser);

        setIsAuthenticated(true);
      } else {
        // User is signed out
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (loading || !isAuthenticated || navigationInProgress.current) {
      // If still loading, not authenticated, or navigation is already in progress, don't proceed
      return;
    }

    if (!currentBusinessType) {
      // If no business type is selected, redirect to onboarding selection
      if (location.pathname !== '/onboarding') {
        navigationInProgress.current = true;
        navigate('/onboarding');
        // Reset the flag after navigation
        setTimeout(() => {
          navigationInProgress.current = false;
        }, 100);
      }
      return;
    }

    if (!onboarded) return;

    const hasViewedCurrentTutorial = onboarded[currentBusinessType];

    // Only navigate if we're not already in the process of navigating
    if (!hasViewedCurrentTutorial && location.pathname !== '/packages') {
      if (location.pathname.includes('onboarding')) {
        // If user is on /onboarding but has a business type selected, redirect to specific onboarding page
        if (location.pathname === '/onboarding') {
          navigationInProgress.current = true;
          navigate(onboardingPages[currentBusinessType]);
          setTimeout(() => {
            navigationInProgress.current = false;
          }, 100);
        }
        return; // Already on specific onboarding page
      }
      // Navigate to the onboarding page specific to the current business type
      navigationInProgress.current = true;
      navigate(onboardingPages[currentBusinessType]);
      setTimeout(() => {
        navigationInProgress.current = false;
      }, 100);
    } else if (
      hasViewedCurrentTutorial &&
      location.pathname.includes('onboarding')
    ) {
      // User has completed onboarding but is still on onboarding page
      navigationInProgress.current = true;
      navigate('/');
      setTimeout(() => {
        navigationInProgress.current = false;
      }, 100);
    }
  }, [
    onboarded,
    currentBusinessType,
    location.pathname,
    isAuthenticated,
    loading,
    navigate,
  ]);

  if (loading) {
    return <div>Loading...</div>; // Or any other loading indicator you prefer
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <Outlet />;
};

export default PrivateRoutes;
